/** @file Sets some or all of a region's transform and size; on a mounted region only scale and size. */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { RegionAtt } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';

const FIELDS = ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'width', 'height'] as const;
/**
 * The fields a mount ignores. The compiler takes the handle in the item's authored frame (compiler mountMatrix ->
 * core itemMountTransform) and draws M · fromLocal(x, y, rotation, scaleX, scaleY) (regionRecord mountedPlacement):
 * M undoes the item's own place and turn, so x, y and rotation cancel while scale, width and height stay (R19-03).
 */
const PLACED = ['x', 'y', 'rotation'] as const;
const value = z
  .object({ x: prim.finite, y: prim.finite, rotation: prim.finite, scaleX: prim.finite, scaleY: prim.finite, width: prim.finite.positive(), height: prim.finite.positive() })
  .partial()
  .strict();

type Value = z.infer<typeof value>;

/** On a mounted region: VALIDATION at `value.<field>` for a new x, y or rotation; INFO when fitLength sets the size. */
function checkMounted(att: RegionAtt, input: Value, ctx: CommandContext): void {
  if (att.mount === undefined) return;
  const placed = PLACED.find((k) => input[k] !== undefined && input[k] !== att[k]);
  if (placed !== undefined) {
    const hint = `${att.name} is mounted on a socket and its handle places it, so x, y and rotation do not move it: move the handle (attachment.setHandle) or the socket (socket.setPoints), or socket.unequip it first; scaleX, scaleY, width and height apply while mounted`;
    throwError('VALIDATION', undefined, { path: `value.${placed}`, hint });
  }
  if (att.mount.fitLength) {
    const message = `${att.name} is mounted with fitLength: the socket length sets its size, so only the aspect of scaleX/scaleY and width/height (and their signs) changes how it draws`;
    ctx.warnings.push({ code: 'INFO', message, params: { attachment: att.name }, path: 'value' });
  }
}

/** attachment.setTransform: regions only; a mounted region takes scale and size, its handle places it (R19-03). */
export const command = defineCommand({
  name: 'attachment.setTransform',
  description:
    'Set a region attachment transform in its slot bone space: value = any of x, y, rotation (degrees), scaleX, scaleY, width, height (> 0); omitted fields keep their value. Mesh attachments are refused (move their vertices instead). A region mounted on a socket keeps its scale and size where the mount draws it; its handle places it, so a new x, y or rotation is refused (move the handle or the socket).',
  input: z.object({ target: z.string().min(1), value }).strict(),
  apply(draft, input, ctx) {
    const att = draft.attachments[ctx.resolve('attachment', input.target)];
    if (!att) return;
    if (att.type !== 'region') throwError('VALIDATION', undefined, { path: 'target', hint: 'mesh attachments have no transform; move their vertices with mesh.setVertices' });
    if (FIELDS.every((k) => input.value[k] === undefined || input.value[k] === att[k])) return;
    checkMounted(att, input.value, ctx);
    Object.assign(att, input.value);
    draft.meta.updatedAt = ctx.now;
  },
});
